import * as React from "react";
import {useIntl} from "react-intl";

type SaveButtonProps = {
    isDirty: boolean;
    isSubmitting: boolean;
    onClick?: () => void;
    type?: "button" | "submit";
}

/**
 * A save button that shows the save state visually:
 * - No changes (disabled): Gray background, no indicator
 * - Has changes: Red background with red dot indicator (white border)
 * - Saving: Disabled, shows "Saving..." text
 */
const SaveButton: React.FC<SaveButtonProps> = ({
    isDirty,
    isSubmitting,
    onClick,
    type = "submit"
}) => {
    const intl = useIntl();

    const isDisabled = !isDirty || isSubmitting;
    const showIndicator = isDirty && !isSubmitting;

    const buttonText = isSubmitting
        ? intl.formatMessage({id: "button.saving"})
        : intl.formatMessage({id: "action.save"});

    return (
        <div className="relative inline-flex">
            <button
                type={type}
                onClick={onClick}
                disabled={isDisabled}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    isDisabled
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-red-600 text-white hover:bg-red-700"
                }`}
            >
                {buttonText}
            </button>
            {showIndicator && (
                <span className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
            )}
        </div>
    );
};

export default SaveButton;
